import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "react-query";
import { Link, useParams } from "react-router-dom";

import { Facility } from "@secondcloset/fulfillment-utils";
import { Common, Fulfillment } from "@secondcloset/types";
import { ASNTable, CheckboxDropdown, SearchInput } from "@secondcloset/web-components";

import { FilterOutlined } from "@ant-design/icons";
import { Space, Typography, DatePicker, Button, Select } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import * as S from "./styles";

import useUrlState from "hooks/application/useUrlState";

import { fetchASNIndex } from "../../../api/fulfillment/asn";
import { useOrganizationContext } from "../../../contextProviders/organization/OrganizationProvider";
import { useFacilityIndex } from "../../../hooks/api/common/useFacilityIndex";
import { fetchInventoryChannels } from "../../../api/fulfillment/product";
import { titleCase } from "../../../components/SelectSalesChannel";

const STATUS_OPTIONS = [
  { key: "draft", value: "draft", label: "Draft" },
  { key: "awaiting", value: "awaiting", label: "Awaiting" },
  { key: "arrived", value: "arrived", label: "Arrived" },
  { key: "completed", value: "completed", label: "Completed" },
  { key: "on_hold", value: "on_hold", label: "On Hold" },
];

const EXCEPTION_OPTIONS = [
  { key: "missing_date", value: "missing_date", label: "Missing Date" },
  { key: "overdue", value: "overdue", label: "Overdue" },
  { key: "under_received", value: "under_received", label: "Under Received" },
  { key: "over_received", value: "over_received", label: "Over Received" },
];

export const getWarehouseOptions = (facilities: Common.Facility<"V2">[]) =>
  Facility.getFulfillmentFacilityList(facilities).map((f) => ({
    key: f.code,
    value: f.code,
    label: f.name,
  }));

const PAGE_SIZE = 10;
type ASN = Fulfillment.ASN;

const FulfillmentASNIndexPage: React.FC = () => {
  const { data: orgDetails, loading: isOrgLoading } = useOrganizationContext();
  const { organization_id } = useParams<{ organization_id: string }>();
  const orgID = orgDetails?.id ?? organization_id;
  const facilityIndex = useFacilityIndex();
  const warehouseOptions = getWarehouseOptions(facilityIndex.data || []);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [status, setStatus] = useUrlState({
    name: "status",
    initialValue: [],
  });
  const [exceptions, setExceptions] = useUrlState({
    name: "exceptions",
    initialValue: [],
  });
  const [inventoryChannel, setInventoryChannel] = useUrlState({
    name: "inventoryChannel",
    initialValue: [],
  });
  const [facility, setFacility] = useUrlState({
    name: "facility",
    initialValue: "all",
  });
  const [searchASN, setSearchASN] = useUrlState({
    name: "search",
    initialValue: "",
  });
  const [searchSKU, setSearchSKU] = useUrlState({
    name: "sku",
    initialValue: "",
  });
  const [searchOrgName, setSearchOrgName] = useUrlState({
    name: "org",
    initialValue: "",
  });
  const [arrivalDate, setArrivalDate] = useUrlState({
    name: "arrival_date",
    initialValue: "",
  });
  const [page, setPage] = useUrlState({ name: "page", initialValue: 1 });
  const [sort, setSort] = useState<{
    order_by?: string;
    direction?: "asc" | "desc";
  }>();

  const channels = (inventoryChannel as string[]).filter((s) => s !== "");
  const showUnassignedChannel = inventoryChannel.includes("");
  const queryOptions = {
    per_page: pageSize,
    page: page || 1,
    ...(orgDetails ? { organization_id: orgID } : { requires: ["organization_name"] }),
    ...(searchASN && { q: String(searchASN).trim() }),
    ...(searchSKU && { sku: String(searchSKU).trim() }),
    ...(searchOrgName && { organization_name: String(searchOrgName).trim() }),
    ...(facility && facility !== "all" && { facility }),
    ...(arrivalDate && { arrival_date: arrivalDate }),
    ...(status?.length && { status }),
    ...(exceptions?.length && { exception: exceptions }),
    ...(sort?.order_by && sort?.direction && { ...sort }),
    ...(channels.length && { inventory_channel: channels }),
    ...(showUnassignedChannel && { inventory_channel_unassigned: true }),
  };

  const ASNIndex = useQuery(["ASNIndex", queryOptions], () => fetchASNIndex(queryOptions), { keepPreviousData: true });

  const { data: channelOptions = [], ...channelQuery } = useQuery(
    ["channels", orgID],
    () => fetchInventoryChannels(orgID),
    {
      enabled: !!orgID,
      select({ data }) {
        const options = data.map((d) => ({ key: d.name, value: d.name, label: titleCase(d.name) }));
        return [...options, { key: "", value: "", label: "Unassigned" }];
      },
    }
  );

  useEffect(() => {
    setPage(1);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [searchASN, searchSKU, searchOrgName]);

  const handleSort = ({ columnKey, order }: SorterResult<ASN>) => {
    const key = columnKey as keyof ASN;
    // status is not supported in server side sorting
    if (key !== "status") {
      setSort({
        order_by: key,
        direction: order ? (order === "ascend" ? "asc" : "desc") : undefined,
      });
    }
    ASNIndex.data?.sort((a: any, b: any) => {
      if (!a[key] || !b[key] || !order) return 0;
      const isAscending = order === "ascend";
      return (isAscending ? a[key] > b[key] : a[key] < b[key]) ? 1 : -1;
    });
  };

  const renderHeaderSection = () => {
    const link = orgDetails ? `/organizations/${orgDetails.id}/wros` : `/fulfillment/wros`;
    return (
      <Space size="large" align="start">
        <Typography.Title level={4}>Advance Shipping Notice</Typography.Title>
        <Link to={link}>Switch to WRO (legacy)</Link>
      </Space>
    );
  };

  const renderMainSearchSection = () => (
    <S.FilterWrapper>
      <SearchInput
        className="search-input"
        placeholder="Search for ASN, Tracking, Container or PO #"
        defaultValue={searchASN}
        onSearch={setSearchASN}
        onClear={() => setSearchASN("")}
      />
      {!orgDetails && (
        <SearchInput
          className="search-input"
          placeholder="Search for Organization"
          defaultValue={searchOrgName}
          onSearch={setSearchOrgName}
          onClear={() => setSearchOrgName("")}
        />
      )}
      <Button
        icon={<FilterOutlined />}
        onClick={() => setIsFilterVisible((state) => !state)}
        style={{ marginLeft: "auto" }}
        type="text"
      >
        {isFilterVisible ? "Hide" : "Show"} Filter
      </Button>
    </S.FilterWrapper>
  );

  const renderFiltersSection = () => (
    <S.FilterWrapper>
      <SearchInput
        placeholder="Search for SKU"
        defaultValue={searchSKU}
        onSearch={setSearchSKU}
        onClear={() => setSearchSKU("")}
        style={{ width: 200 }}
      />
      <CheckboxDropdown
        options={STATUS_OPTIONS}
        defaultText="All Status"
        selectedOptionKeys={status ?? []}
        setSelectedOptionKeys={setStatus}
      />
      <CheckboxDropdown
        options={EXCEPTION_OPTIONS}
        defaultText="All Exceptions"
        selectedOptionKeys={exceptions ?? []}
        setSelectedOptionKeys={setExceptions}
      />
      <CheckboxDropdown
        options={channelOptions}
        defaultText="All Channels"
        selectedOptionKeys={inventoryChannel ?? []}
        setSelectedOptionKeys={setInventoryChannel}
        loading={channelQuery.isLoading}
      />
      <Select
        options={[{ key: "all", value: "all", label: "All Warehouse" }, ...warehouseOptions]}
        value={facility}
        onChange={setFacility}
        style={{ width: "200px" }}
        loading={facilityIndex.isLoading}
      />
      <DatePicker
        placeholder="Arrival Date"
        onChange={(_, dateString: string) => setArrivalDate(dateString)}
        style={{ width: "200px" }}
      />
    </S.FilterWrapper>
  );

  return (
    <S.Container>
      <Helmet title="Sierra - Advance Shipping Notice" />
      <Space direction="vertical" style={{ width: "100%" }}>
        {renderHeaderSection()}
        {renderMainSearchSection()}
        {isFilterVisible && renderFiltersSection()}
      </Space>
      <ASNTable
        isAdmin
        showOrganizationColumn={!orgDetails?.id}
        ASNList={ASNIndex.data ?? []}
        isLoading={ASNIndex.isPreviousData || ASNIndex.isLoading || facilityIndex.isLoading || isOrgLoading}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        renderASNLinkComponent={(displayText: string, id: string) => (
          <Link to={`/fulfillment/asns/${id}`}>{displayText}</Link>
        )}
        renderOrgLinkComponent={(displayText: string, id: string) => (
          <Link to={`/organizations/${id}`}>{displayText}</Link>
        )}
        facilities={facilityIndex.data ?? []}
      />
    </S.Container>
  );
};

export default FulfillmentASNIndexPage;
